import * as actionTypes from './actionTypes';
import { RivalActionTypes } from './actionTypes';
import { HttpError } from '../../config/Axios/axios-instance';
import { Rival } from '../../domain/Rival';
import { ListResults } from '../../common/List/List';

export type RivalStateType = {
  rivalsList: ListResults<Rival> | null;
  rivalsLoading: boolean;
  rivalsError: HttpError;
  rivalsListUpdateNeeded: boolean;
  rivalCreateLoading: boolean;
  rivalCreateError: HttpError;
  rivalCreateSuccess: boolean;
  createdRival: Rival | null;
  rivalUpdateLoading: boolean;
  rivalUpdateError: HttpError;
  rivalUpdateSuccess: boolean;
  rivalDeleteLoading: boolean;
  rivalDeleteError: HttpError;
  rivalDeleteSuccess: boolean;
  rival: Rival | null;
  rivalLoading: boolean;
  rivalError: HttpError;
  allRivals: Rival[];
  allRivalsLoading: boolean;
  allRivalsError: HttpError;
};

export type RivalActionType = RivalStateType & {
  type: RivalActionTypes;
};

export const initialState: RivalStateType = {
  rivalsList: null,
  rivalsLoading: true,
  rivalsError: null,
  rivalsListUpdateNeeded: false,
  rivalCreateLoading: false,
  rivalCreateError: null,
  rivalCreateSuccess: false,
  createdRival: null,
  rivalUpdateLoading: false,
  rivalUpdateError: null,
  rivalUpdateSuccess: false,
  rivalDeleteLoading: false,
  rivalDeleteError: null,
  rivalDeleteSuccess: false,
  rival: null,
  rivalLoading: false,
  rivalError: null,
  allRivals: [],
  allRivalsError: null,
  allRivalsLoading: false,
};

const fetchRivalsStart = (state: RivalStateType): RivalStateType => ({
  ...state,
  rivalsLoading: true,
});

const fetchRivalsSuccess = (
  state: RivalStateType,
  action: RivalActionType,
): RivalStateType => ({
  ...state,
  rivalsList: action.rivalsList,
  rivalsLoading: false,
  rivalsError: null,
  rivalsListUpdateNeeded: false,
});

const fetchRivalsFail = (
  state: RivalStateType,
  action: RivalActionType,
): RivalStateType => ({
  ...state,
  rivalsError: action.rivalsError,
  rivalsLoading: false,
});

const fetchAllRivalsStart = (state: RivalStateType): RivalStateType => ({
  ...state,
  allRivalsLoading: true,
});

const fetchAllRivalsSuccess = (
  state: RivalStateType,
  action: RivalActionType,
): RivalStateType => ({
  ...state,
  allRivals: action.allRivals,
  allRivalsLoading: false,
  allRivalsError: null,
});

const fetchAllRivalsFail = (
  state: RivalStateType,
  action: RivalActionType,
): RivalStateType => ({
  ...state,
  allRivalsError: action.allRivalsError,
  allRivalsLoading: false,
});

const fetchRivalStart = (state: RivalStateType): RivalStateType => ({
  ...state,
  rivalLoading: true,
  rivalCreateSuccess: false,
  rivalCreateError: null,
});

const fetchRivalSuccess = (
  state: RivalStateType,
  action: RivalActionType,
): RivalStateType => ({
  ...state,
  rival: action.rival,
  rivalLoading: false,
  rivalError: null,
});

const fetchRivalFail = (
  state: RivalStateType,
  action: RivalActionType,
): RivalStateType => ({
  ...state,
  rivalError: action.rivalError,
  rivalLoading: false,
});

const createRivalStart = (state: RivalStateType): RivalStateType => ({
  ...state,
  rivalCreateLoading: true,
});

const createRivalSuccess = (
  state: RivalStateType,
  action: RivalActionType,
): RivalStateType => ({
  ...state,
  rivalCreateLoading: false,
  rivalCreateError: null,
  rivalCreateSuccess: true,
  createdRival: action.createdRival,
});

const createRivalFail = (
  state: RivalStateType,
  action: RivalActionType,
): RivalStateType => ({
  ...state,
  rivalCreateLoading: false,
  rivalCreateError: action.rivalCreateError,
});

const createRivalReset = (state: RivalStateType): RivalStateType => ({
  ...state,
  rivalCreateLoading: false,
  rivalCreateError: null,
  rivalCreateSuccess: false,
  createdRival: null,
});

const updateRivalStart = (state: RivalStateType): RivalStateType => ({
  ...state,
  rivalUpdateLoading: true,
  rivalUpdateSuccess: false,
});

const updateRivalSuccess = (state: RivalStateType): RivalStateType => ({
  ...state,
  rivalUpdateLoading: false,
  rivalUpdateError: null,
  rivalUpdateSuccess: true,
});

const updateRivalFail = (
  state: RivalStateType,
  action: RivalActionType,
): RivalStateType => ({
  ...state,
  rivalUpdateLoading: false,
  rivalUpdateError: action.rivalUpdateError,
});

const deleteRivalStart = (state: RivalStateType): RivalStateType => ({
  ...state,
  rivalDeleteLoading: true,
});

const deleteRivalSuccess = (state: RivalStateType): RivalStateType => ({
  ...state,
  rivalDeleteLoading: false,
  rivalDeleteError: null,
  rivalDeleteSuccess: true,
  rivalsListUpdateNeeded: true,
});

const deleteRivalFail = (
  state: RivalStateType,
  action: RivalActionType,
): RivalStateType => ({
  ...state,
  rivalDeleteLoading: false,
  rivalDeleteError: action.rivalDeleteError,
});

const refreshRivalList = (state: RivalStateType): RivalStateType => ({
  ...initialState,
  rivalsListUpdateNeeded: true,
});

const resetRivalStore = (state: RivalStateType): RivalStateType => ({
  ...initialState,
});

const completelyResetRivalStore = (): RivalStateType => ({
  ...initialState,
});

const reducer = (state = initialState, action: RivalActionType) => {
  switch (action.type) {
    case actionTypes.FETCH_RIVALS_START:
      return fetchRivalsStart(state);
    case actionTypes.FETCH_RIVALS_SUCCESS:
      return fetchRivalsSuccess(state, action);
    case actionTypes.FETCH_RIVALS_FAIL:
      return fetchRivalsFail(state, action);
    case actionTypes.FETCH_ALL_RIVALS_START:
      return fetchAllRivalsStart(state);
    case actionTypes.FETCH_ALL_RIVALS_SUCCESS:
      return fetchAllRivalsSuccess(state, action);
    case actionTypes.FETCH_ALL_RIVALS_FAIL:
      return fetchAllRivalsFail(state, action);
    case actionTypes.FETCH_RIVAL_START:
      return fetchRivalStart(state);
    case actionTypes.FETCH_RIVAL_SUCCESS:
      return fetchRivalSuccess(state, action);
    case actionTypes.FETCH_RIVAL_FAIL:
      return fetchRivalFail(state, action);
    case actionTypes.CREATE_RIVAL_START:
      return createRivalStart(state);
    case actionTypes.CREATE_RIVAL_SUCCESS:
      return createRivalSuccess(state, action);
    case actionTypes.CREATE_RIVAL_FAIL:
      return createRivalFail(state, action);
    case actionTypes.CREATE_RIVAL_RESET:
      return createRivalReset(state);
    case actionTypes.UPDATE_RIVAL_START:
      return updateRivalStart(state);
    case actionTypes.UPDATE_RIVAL_SUCCESS:
      return updateRivalSuccess(state);
    case actionTypes.UPDATE_RIVAL_FAIL:
      return updateRivalFail(state, action);
    case actionTypes.DELETE_RIVAL_START:
      return deleteRivalStart(state);
    case actionTypes.DELETE_RIVAL_SUCCESS:
      return deleteRivalSuccess(state);
    case actionTypes.DELETE_RIVAL_FAIL:
      return deleteRivalFail(state, action);
    case actionTypes.REFRESH_RIVAL_LIST:
      return refreshRivalList(state);
    case actionTypes.RESET_RIVAL_STORE:
      return resetRivalStore(state);
    case actionTypes.LOGOUT:
      return completelyResetRivalStore();
    default:
      return state;
  }
};

export default reducer;
